/**
 * @file - Uber authorize component.
 */

import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { DriverService } from '../../../core/services/driver/driver.service';
import { UiMessageCodes } from '../../../shared/constants/info-messages.constant';
import { UserDataService } from '../../../core/services/user-data/user-data.service';
import { DialogHandlerService } from '../../../core/services/dialog-handler/dialog-handler.service';
import { environment } from '../../../../environments/environment';
import { FunnelGuardSyncService } from '../../../core/services/driver/funnel-guard-sync.service';
import { RoutePaths } from '../../../shared/constants/route-paths.constant';
import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';
import { isReferredByUberPartner, startUserSessionMonitor } from '../../../shared/utitlity-functions/utility-functions';

/**
 * Uber profile interface.
 */
interface UberProfile {
    first_name: string;
    last_name: string;
    email: string;
    picture: string;
    phone_number: string;
}

/**
 *
 */
@Component({
    standalone: false,
    selector: 'uber-authorize',
    templateUrl: './authorize.component.html',
    styleUrls: ['./authorize.component.scss'],
})
export class AuthorizeComponent implements OnInit {
    code: string = '';
    state: any = {};
    rawState: string = '';
    error: string = '';

    isVerifying: boolean = true;

    uberProfile: UberProfile | null = null;
    countdown: number = 10;
    secondsMapping: { [key: string]: string } = {
        '=0': '0 seconds',
        '=1': '1 second',
        other: '# seconds',
    };
    uberLink: string = environment.UBER_LINK;

    showUberProfile: boolean = false;
    isUberConnectionSuccess: boolean | null = null;
    uberConnectionError: string = '';

    shouldRedirect: boolean = false;
    isApplication: boolean = false;
    isReferredByUberPartner$ = new BehaviorSubject<boolean>(false);

    /**
     * Constructor.
     *
     * @param {DriverService} driverService - Driver service.
     * @param {UserDataService} userDataService - User data service.
     * @param {DialogHandlerService} dialogService - Dialog service.
     * @param {UserDataService} userService - User service.
     */
    constructor(
        private readonly driverService: DriverService,
        private readonly userDataService: UserDataService,
        private readonly dialogService: DialogHandlerService,
        private readonly userService: UserDataService,
        private readonly funnelService: FunnelGuardSyncService,
        private readonly cdr: ChangeDetectorRef
    ) {
        // Reset funnel status guard
        this.funnelService.setFunnelGuardSync(true);

        // Get the URL parameters
        const queryString = globalThis.location.search;
        const urlParams = new URLSearchParams(queryString);
        this.code = urlParams.get('code') || '';
        this.rawState = urlParams.get('state') || '';
        this.state = this.rawState ? JSON.parse(atob(this.rawState)) : {};
        this.error = urlParams.get('error') || '';

        this.isApplication = this.state?.isApplication || false;
        this.isReferredByUberPartner$.next(this.state?.isReferredByUberPartner);

        if (this.state?.sourcePage?.toUpperCase() == 'TWO_FACTOR_CODE') {
            this.state.sourcePage = 'SIGNUP';
        }
        if (
            this.state?.sourcePage?.toUpperCase() == 'UBER-CONNECT' ||
            this.state?.sourcePage?.toUpperCase() == 'LINK-UBER'
        ) {
            this.state.sourcePage = 'DASHBOARD';
        }

        if (!this.code) {
            if (this.error === 'access_denied') {

                if (this.isReferredByUberPartner$.value) {

                    this.isVerifying = false;
                    this.showUberProfile = false;
                    this.isUberConnectionSuccess = false;
                    this.shouldRedirect = false;
                    this.uberConnectionError =
                        'You have denied access to your Uber account. Connect your Uber account to proceed with the application.';

                } else {

                    this.isVerifying = false;
                    this.showUberProfile = false;
                    this.isUberConnectionSuccess = false;
                    this.shouldRedirect = true;
                    this.uberConnectionError =
                        "You have denied access to your Uber account. To connect your Uber account, please try again or click on the 'Proceed to the application' button below.";

                    // Countdown
                    setInterval(() => {
                        this.countdown--;
                        this.cdr.detectChanges();
                        if (this.countdown === 0) {
                            globalThis.location.href = `${globalThis.location.origin}${RoutePaths.ROOT_PATH}`;
                        }
                    }, 1000);
                }
            } else if (this.error === 'uber_partner_referral_not_connected') {

                this.isVerifying = false;
                this.showUberProfile = false;
                this.isUberConnectionSuccess = false;
                this.uberConnectionError =
                    'An error occurred while connecting your Uber account. Connect your Uber account to proceed with the application.';
                this.isReferredByUberPartner$.next(true);

            } else {
                // Show error message
                this.isVerifying = false;
                this.showUberProfile = false;
                this.isUberConnectionSuccess = false;
                this.uberConnectionError =
                    "An error occurred while connecting your Uber account. To connect your Uber account, please try again or click on the 'Proceed to the application' button below.";
            }
            return;
        }

        // Call the backend and pass the code
        this.verifyUberCode();
    }

    ngOnInit(): void {
        startUserSessionMonitor();
    }

    /**
     * Verify the Uber code.
     */
    verifyUberCode(): void {
        this.isVerifying = true;
        this.shouldRedirect = false;

        this.driverService
            .connectUberAccount({
                code: this.code,
                source: this.state?.sourcePage,
                state: this.rawState,
            })
            .subscribe({
                /**
                 * Next.
                 *
                 * @param {any} response - Response.
                 */
                next: (response: any) => {
                    this.isVerifying = false;
                    this.shouldRedirect = true;

                    // Uber connected successfully
                    if (response.success) {
                        this.showUberProfile = true;
                        this.isUberConnectionSuccess = true;
                        this.cdr.detectChanges();
                        // If we get a cohort with 0 lifetime trips or Uber account with 0 recent trips show a pop-up
                        if (
                            (response?.data?.eligibility_info
                                ?.lifetime_trips_cohort ===
                                environment.uberLifetimeZeroTrips) ||
                            (response?.data?.eligibility_info?.recent_online_activity_cohort === environment.uberRecentZeroTrips)
                        ) {
                            let message;
                            if (response?.data?.eligibility_info
                                ?.lifetime_trips_cohort ===
                                environment.uberLifetimeZeroTrips) {
                                (this.state.sourcePage == 'SIGNUP' || this.state.sourcePage == 'info'
                                    || this.state.sourcePage == 'profile' || this.state.sourcePage == 'payment'
                                    || this.state.sourcePage == 'proof_of_rides' || this.state.sourcePage == 'two_factor_code'
                                    || this.state.sourcePage == 'uber-connect-required'
                                ) ?
                                    message = 'This Uber account has 0 trips and you will not be approved for a tablet. Please connect an active Uber account with trips.' :
                                    message = 'You linked an account with 0 lifetime trips. Please link your active Uber account.';
                            } else {
                                (this.state.sourcePage == 'SIGNUP' || this.state.sourcePage == 'info'
                                    || this.state.sourcePage == 'profile' || this.state.sourcePage == 'payment'
                                    || this.state.sourcePage == 'proof_of_rides' || this.state.sourcePage == 'two_factor_code'
                                    || this.state.sourcePage == 'uber-connect-required'
                                ) ?
                                    message = 'This Uber account has no recent trips. You will not be approved for a tablet if you do not actively drive with Uber. Would you like to connect another account?' :
                                    message = 'You linked an account with no recent Uber activity. Please link your active Uber account.';
                            }
                            this.dialogService
                                .confirmActionWithUberRedirect({
                                    title: 'Are you sure you want to link this account?',
                                    message: message,
                                    cancelBtnText: 'Link new account',
                                    confirmBtnText: 'Proceed with same account',
                                })
                                .subscribe((data) => {
                                    if (data === true) {
                                        this.isVerifying = true;
                                        this.showUberProfile = false;
                                        this.isUberConnectionSuccess = false;
                                        this.cdr.detectChanges();
                                        this.driverService
                                            .removeUberAccount(
                                                this?.state?.sourcePage
                                            )
                                            .subscribe(() => {
                                                this.userService
                                                    .fetchUserSummary()
                                                    .subscribe(() => {
                                                        let stateDict = {
                                                            sourcePage:
                                                                globalThis.location.pathname
                                                                    .split('/')
                                                                    .pop(),
                                                            isApplication: [
                                                                'tos_complete',
                                                                'driver_info_complete',
                                                                'profile_complete',
                                                                'payment_complete',
                                                            ].includes(
                                                                localStorage.getItem(
                                                                    'status'
                                                                ) || ''
                                                            ),
                                                            isReferredByUberPartner: isReferredByUberPartner(this.userService.userSummaryDetails?.referral_info?.referred_by),
                                                        };
                                                        const state = btoa(
                                                            JSON.stringify(
                                                                stateDict
                                                            )
                                                        );
                                                        // Go to the Uber link
                                                        globalThis.open(
                                                            `${this.uberLink}&state=${state}`,
                                                            '_self'
                                                        );
                                                    });
                                            });
                                    } else {
                                        this.link_and_redirect(response);
                                    }
                                });
                        } else {
                            this.link_and_redirect(response);
                        }
                    }
                },
                /**
                 * Error.
                 *
                 * @param {any} error - Error.
                 */
                error: (error: any) => {
                    console.error('Error', error);
                    this.isVerifying = false;
                    this.showUberProfile = false;
                    this.isUberConnectionSuccess = false;
                    if (error?.error?.ui_message_code) {
                        // Show error message
                        this.uberConnectionError =
                            UiMessageCodes[error.error.ui_message_code];
                    }
                    this.cdr.detectChanges();
                },
            });
    }

    /**
     * Proceed to the next step.
     */
    proceed(): void {
        globalThis.location.href = `${globalThis.location.origin}${RoutePaths.ROOT_PATH}`;
    }

    /**
     * Link and redirect
     *
     * @param {any} response - Response.
     */
    link_and_redirect(response: any) {
        this.showUberProfile = true;
        this.isUberConnectionSuccess = true;
        this.uberProfile = response.data;
        this.cdr.detectChanges();
        this.userDataService.setUserSummary(true);
        // Countdown
        setInterval(() => {
            this.countdown--;
            this.cdr.detectChanges();
            if (this.countdown === 0) {
                if (this.state?.sourcePage?.toUpperCase() == 'LINK-UBER') {
                    globalThis.location.href = `${globalThis.location.origin}/${RoutePaths.DASHBOARD_MY_ACCOUNT_DRIVER_INFO}`;
                }
                else if (this.state?.sourcePage.toUpperCase() == 'UBER-CONNECT-REQUIRED') {
                    globalThis.location.href = `${globalThis.location.origin}/${RoutePaths.PROOF_OF_RIDES}`;
                }
                else {
                    globalThis.location.href = `${globalThis.location.origin}${RoutePaths.ROOT_PATH}`;
                }
            }
        }, 1000);
    }
}
